import React, { useRef, useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import chinaMapData from "./data/china_map.json";
import { useDimensions } from "./useDimensions";
import useIsMobile from "./useIsMobile";

gsap.registerPlugin(ScrollTrigger);

const cities = [
    { name: "北京", coordinates: [116.4074, 39.9042] },
    { name: "上海", coordinates: [121.4737, 31.2304] },
    { name: "广州", coordinates: [113.2644, 23.1291] },
    { name: "成都", coordinates: [104.0668, 30.5728] },
    { name: "武汉", coordinates: [114.3055, 30.5928] }
];

const BubbleMap = ({ data }) => {
    const containerRef = useRef(null);
    const svgRef = useRef();
    const dimensions = useDimensions(containerRef);
    const viewRef = useRef("cases");

    const articleRef = useRef(null);

    const [chartTitle, setChartTitle] = useState("中国各省袭击事件数量");

    const isMobile = useIsMobile();

    const { caseCounts, victimCounts, totalDeaths, totalInjured } = useMemo(() => {
        const counts = {
            caseCounts: {},
            victimCounts: {},
            totalDeaths: {},
            totalInjured: {},
        };

        data.forEach(d => {
            counts.caseCounts[d.province] = (counts.caseCounts[d.province] || 0) + 1;
            counts.victimCounts[d.province] = (counts.victimCounts[d.province] || 0) + d.victims;
            counts.totalDeaths[d.province] = (counts.totalDeaths[d.province] || 0) + d.deaths;
            counts.totalInjured[d.province] = (counts.totalInjured[d.province] || 0) + d.injured;
        });

        return counts;
    }, [data]);

    const { radiusScale, victimRadiusScale, colorScale, victimColorScale } = useMemo(() => {
        const maxCases = d3.max(Object.values(caseCounts)) || 1;
        const maxVictims = d3.max(Object.values(victimCounts)) || 1;

        return {
            radiusScale: d3.scaleSqrt().domain([0, maxCases]).range([0, 30]),
            victimRadiusScale: d3.scaleSqrt().domain([0, maxVictims]).range([0, 30]),
            colorScale: d3.scaleSequential(d3.interpolateReds).domain([0, maxCases]),
            victimColorScale: d3.scaleSequential(d3.interpolatePurples).domain([0, maxVictims]),
        };
    }, [caseCounts, victimCounts]);

    // Memoize projection and path generator
    const { projection, pathGenerator } = useMemo(() => {
        if (!dimensions.width || !dimensions.height) return {};
        const proj = d3.geoMercator().fitSize([dimensions.width, dimensions.height], chinaMapData);
        return {
            projection: proj,
            pathGenerator: d3.geoPath().projection(proj),
        };
    }, [dimensions]);

    useEffect(() => {
        if (!data || data.length === 0) return;
        
        const { width, height } = dimensions;
        if (width === 0 || height === 0) return;

        // const projection = d3.geoMercator().fitSize([width, height], chinaMapData);
        // const pathGenerator = d3.geoPath().projection(projection);

        // const caseCounts = data.reduce((acc, d) => {
        //     acc[d.province] = (acc[d.province] || 0) + 1;
        //     return acc;
        // }, {});

        // const caseCounts = {};
        // const victimCounts = {};
        // const totalDeaths = {};
        // const totalInjured = {};

        // data.forEach(d => {
        //     caseCounts[d.province] = (caseCounts[d.province] || 0) + 1;
        //     victimCounts[d.province] = (victimCounts[d.province] || 0) + d.victims;
        //     totalDeaths[d.province] = (totalDeaths[d.province] || 0) + d.deaths;
        //     totalInjured[d.province] = (totalInjured[d.province] || 0) + d.injured;
        // });

        // const radiusScale = d3.scaleSqrt()
        //     .domain([0, d3.max(Object.values(caseCounts))])
        //     .range([0, 30]);
        // const victimRadiusScale = d3.scaleSqrt()
        //     .domain([0, d3.max(Object.values(victimCounts))])
        //     .range([0, 30]);

        // const colorScale = d3
        //       .scaleSequential(d3.interpolateReds)
        //       .domain([0, d3.max(Object.values(caseCounts))]);
        // const victimColorScale = d3.scaleSequential(d3.interpolatePurples).domain([0, d3.max(Object.values(victimCounts))]);

        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr("height", height);

        svg.selectAll("*").remove();

        // Draw base map
        svg.selectAll("path")
            .data(chinaMapData.features)
            .join("path")
            .attr("d", pathGenerator)
            .attr("fill", "#272727")
            .attr("stroke", "#C7C7C7")
            .attr("stroke-width", 0.5)
            .attr("stroke-linecap", "round")
            .attr("pointer-events", "none");


        // Create bubbles
        const bubbles = svg.selectAll("circle")
            .data(chinaMapData.features)
            .join("circle")
            .attr("class", "bubblemap")
            .attr("cx", d => pathGenerator.centroid(d)[0])
            .attr("cy", d => pathGenerator.centroid(d)[1])
            // .attr("r", d => radiusScale(caseCounts[d.properties.name] || 0))
            .attr("r", d => isMobile ?  radiusScale(caseCounts[d.properties.name] * 0.3) : radiusScale(caseCounts[d.properties.name]))
            .attr("fill", d => colorScale(caseCounts[d.properties.name] || 0))
            .attr("opacity", 0.8)
            .attr("stroke", "#fff")
            .attr("stroke-width", 1);


        const guangdongFeature = chinaMapData.features.find(d => d.properties.name === "广东");

        if (guangdongFeature) {
            const [x, y] = pathGenerator.centroid(guangdongFeature);

            // Create a callout group
            const calloutGroup = svg.append("g").attr("class", "callout");

            // Add a circle around Guangdong
            calloutGroup.append("circle")
                .attr("cx", x)
                .attr("cy", y)
                // .attr("r", 35)
                .attr("r", isMobile ? 35 * 0.6 : 35)
                .attr("fill", "none")
                .attr("stroke", "white")
                .attr("stroke-width", 2)
                .attr("stroke-opacity", 0.9)
                .attr("stroke-dasharray", "6 4");

            // const note = "广东省报告的事件数量最多，\n远远多于其他省份";
            // const lines = note.split("\n");
            
            // const textElement = calloutGroup.append("text")
            //     .attr("class", "callout")
            //     .attr("x", x + 40)
            //     .attr("y", y + 25);
            
            // lines.forEach((line, i) => {
            //     textElement.append("tspan")
            //         .attr("x", x + 40) // Keep x constant
            //         .attr("dy", i === 0 ? 0 : 17)
            //         .text(line);
            // });

            svg.append("foreignObject")
                .attr("class", "callout")
                // .attr("x", x + 40)
                // .attr("y", y + 25)
                .attr("x", x + Math.min(width * 0.05, 50))
                .attr("y", y + Math.min(height * 0.08, 20))
                .attr("width", Math.max(width * 0.15, 120))
                .attr("height", Math.max(height * 0.1, 60)) 
                // .attr("width", 170)
                // .attr("height", 100)
                .append("xhtml:div")
                .style("word-wrap", "break-word")
                .style("text-align", "left")
                .html("广东省报告的事件数量最多，远远多于其他省份")
        }



        // Tooltip setup
        const Tooltip = d3.select("body")
            .append("div")
            .attr("class", "tooltip")
            .style("opacity", 0);

        const handleMouseOver = (event, d) => {
            const provinceName = d.properties.name;
            const count = caseCounts[provinceName] || 0;
            const deaths = totalDeaths[provinceName] || 0;
            const injured = totalInjured[provinceName] || 0;

            d3.select(event.currentTarget)
                .raise()
                .transition()
                .duration(200)
                .attr("opacity", 1);
            
            svg.append("path")
            .data([d])
            .attr("d", pathGenerator)
            .attr("class", "hoverStroke")
            .attr("fill", "none")
            .attr("stroke", "#fff")
            .attr("stroke-width", 2)
            .attr("stroke-opacity",0.5)
            .attr("stroke-linecap", "round")
            .attr("pointer-events", "none");

            Tooltip.transition().duration(200).style("opacity", 1);
            Tooltip.html(
                `
                <b>${provinceName}</b> ${viewRef.current === "cases" ? `<span class='tooltip__num'>${count}</span>次杀伤案例` : `被害者种共<span class='tooltip__num'>${deaths}</span>死，<span class='tooltip__num'>${injured}</span>伤`}
                `
            )
                .style("left", `${event.pageX + 10}px`)
                .style("top", `${event.pageY - 28}px`);
            
        };

       
        const handleMouseMove = (event) => {
            Tooltip
                .style("left", `${event.pageX + 10}px`)
                .style("top", `${event.pageY - 28}px`);
        }

        const handleMouseOut = (event, d) => {

            d3.select(event.currentTarget)
                .transition()
                .duration(200)
                .attr("opacity", 0.8)
                .attr("stroke-width", 1);

            Tooltip.transition().duration(200).style("opacity", 0);

            svg.selectAll("path.hoverStroke").remove();
        };

        

        
        
        bubbles
        .on("mouseover", handleMouseOver)
        .on("mousemove", handleMouseMove)
        .on("mouseout", handleMouseOut);


        //scroll

        

        ScrollTrigger.create({
            trigger: containerRef.current,
            start: isMobile ? "300% top" : "+=300",
            end: isMobile ? "250% bottom" : "bottom bottom", 
            // start: "+=300" ,
            // end: "bottom bottom",
            // markers:true,
            scrub: true,
            onUpdate: (self) => {
                const progress = self.progress;
                let currentView = progress < 0.5 ? "cases" : "victims";

                if (viewRef.current !== currentView) {
                    viewRef.current = currentView;
                    setChartTitle(currentView === "cases" ? "中国各省袭击事件数量" : "中国各省被害者人数")
                }

                bubbles.transition().duration(500)
                    .attr("r", d => {
                        const caseR = isMobile ? radiusScale(caseCounts[d.properties.name] * 0.3) : radiusScale(caseCounts[d.properties.name]);
                        const victimR = isMobile ? victimRadiusScale(victimCounts[d.properties.name] * 0.3) : victimRadiusScale(victimCounts[d.properties.name]);
                        return caseR + (victimR - caseR) * progress;
                    })
                    .attr("fill", d => {
                        const caseColor = colorScale(caseCounts[d.properties.name] || 0);
                        const victimColor = victimColorScale(victimCounts[d.properties.name] || 0);
                        return d3.interpolateRgb(caseColor, victimColor)(progress);
                    });
            },
        });

        

        const cityGroup = svg.append("g").attr("class", "city-markers");

        cityGroup.selectAll("circle")
            .data(cities)
            .join("circle")
            .attr("cx", d => projection(d.coordinates)[0]) // Convert coordinates to SVG
            .attr("cy", d => projection(d.coordinates)[1])
            .attr("r", 1.5)
            .attr("fill", "white")
            .attr("stroke", "#fff")
            .attr("stroke-width", 1)
            .attr("opacity", 0.8);


        // Add City Labels
        cityGroup.selectAll("text")
            .data(cities)
            .join("text")
            .attr("x", d => projection(d.coordinates)[0] + 3)
            .attr("y", d => projection(d.coordinates)[1] - 2)
            .text(d => d.name)
            .attr("fill", "white")
            .attr("text-anchor", "start");
        

    }, [data, dimensions, isMobile]);
    

    return (
        <>
        <div className="center-container">
            <div className="article-body">
                <h2>广东省是无差别袭击事件的高发省份</h2>
            </div>
        </div>
            <div className="grid-container-right"> 
                
                <div className="article-body" ref={articleRef}  style={{ pointerEvents:"none" }}>
                    <div className="spacerdiv__tall" />
                    <div style={{ pointerEvents:"auto" }}>
                    <div id="bubble-map-text">
                        <p>歪脑从目前在互联网上可查的公开资料整理发现，广东，湖南和山东是中国发生无差别袭击事件最集中的三个省份，其中广东省远高于中国其它省市。广州是中国媒体重镇，改革开放后，逐渐孕育了《南方周末》，《南方都市报》等有影响力的媒体，相应的，广州舆论环境更开放。对中国其它省份，发生在广东省的新闻被管控与屏蔽的概率可能更低。这也可能是导致在公开资料上，广东省呈现出有更多无差别袭击事件的一个因素。</p>
    
    
                        <p>从其它数据上，歪脑参照2023年中国国家统计局公布的各省市常住人口、领取失业保险实际人数、领取养老保险人数与人均GDP和最近一年的失业率（2021年），发现无差别事件和常住人口有显著的关联。也就是说，人口稠密的地区，更有可能发生无差别袭击事件。但因中国官方的公开数据有限，我们不能肯定与其它因素无关。</p>
                        <p>例如外界总部位于华盛顿的《中国异议监测》<i>(China Dissent Monitor)</i> 的最新<a href="https://freedomhouse.org/report/china-dissent-monitor/2024/issue-9-july-september-2024" target="_blank">数据</a>显示，从2024年7月～9月，中国共有937件抗议活动，相比2023年同一时间，有27%的增长。在中国经济下行的背景下，民间有越来越多有关消费者和投资者之间的抗议。其中广东省也是中国民间抗议活动发生最多的省份，山东列举第二，与无差别袭击事件的高发地有一定的吻合度。但因缺乏更多研究数据的支撑，我们无法肯定两者存在直接的关联性。</p>
                    </div>

                    { isMobile ? <div style={{ height:"60vh" }} /> : ``}

                    </div>
                    <div className="spacerdiv__tall" />
                </div>
                
                <div ref={containerRef} className="container-ref mobile-sticky" style={{ height:"75vh"}}>
                    <h4 style={{ transform:"translate(0,120px)"}}>{chartTitle}</h4>
                    <svg ref={svgRef}></svg>
                </div>
    
            </div>
        </>
    );
};

export default BubbleMap;
